import React from 'react'
import { CrossClientProgress } from '../../Progress/CrossClientProgress'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientResultsExecution } from '../../../../store/crossClient/crossClientResults'
import { CrossClientQueryPhases } from '../../../../models/CrossClient'
import { CrossClientQueryRunningInfo } from './ProgressInfo/CrossClientQueryRunningInfo'
import { CrossClientQueryStoppedInfo } from './ProgressInfo/CrossClientQueryStoppedInfo'
import { CrossClientQueryFinishedInfo } from './ProgressInfo/CrossClientQueryFinishedInfo'

export interface CrossClientSidebarProgressProps {
  fullMatchResults: number
  partialMatchResults: number
  notMatchingResults: number
  unableToMatchResults: number
}

export const CrossClientSidebarProgress: React.FC<CrossClientSidebarProgressProps> = ({
  fullMatchResults,
  partialMatchResults,
  notMatchingResults,
  unableToMatchResults,
}) => {
  const execution = useAppSelector(selectCrossClientResultsExecution)

  const running = execution?.phase === CrossClientQueryPhases.PROCESSING || execution?.phase === CrossClientQueryPhases.QUEUED
  const stopped = execution?.phase === CrossClientQueryPhases.CANCELED

  if (!execution) {
    return null
  }

  const totalResults = execution.resultCount
  const processedResults = fullMatchResults + partialMatchResults + notMatchingResults + unableToMatchResults
  const resultPercentage = totalResults ? Math.floor((processedResults / totalResults) * 100) : 0
  const allResultsFinished = resultPercentage === 100

  return (
    <div className='d-flex flex-column gap-3 px-3 py-4 border border-1 zocks-border-color-gray-200 zocks-border-1.0 bg-white'>
      <div className='d-flex gap-3'>
        {!totalResults ? (
          <div className='skeleton zocks-border-1.0' style={{ width: '4rem', height: '4rem' }} />
        ) : (
          <CrossClientProgress
            className='cross-client-progress-chart-container'
            total={totalResults}
            done={processedResults}
            showPercentage
            doneColor={stopped && !allResultsFinished ? 'var(--zocks-gray-500)' : 'var(--zocks-primary)'}
          />
        )}
        <div className='d-flex flex-column justify-content-center'>
          <div className='paragraph-medium'>
            Checked <strong>{processedResults}</strong> {execution.resultCount ? `of ${execution.resultCount.toLocaleString()}` : ''} clients
          </div>
          {running ? (
            <CrossClientQueryRunningInfo execution={execution} />
          ) : stopped && !allResultsFinished ? (
            <CrossClientQueryStoppedInfo execution={execution} />
          ) : execution.startedAt && execution.finishedAt ? (
            <CrossClientQueryFinishedInfo execution={execution} />
          ) : null}
        </div>
      </div>
      {!stopped && totalResults !== processedResults ? (
        <div className='d-flex zocks-gap-0.75 zocks-p-0.75 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-200 zocks-border-0.5 align-items-center'>
          <i className='bi bi-lightbulb fs-6' />
          <div className='paragraph-small'>
            <span className='medium'>Tip:</span> Feel free to close this page. Your search will keep running in the background.
          </div>
        </div>
      ) : stopped && !allResultsFinished ? (
        <div className='d-flex zocks-gap-0.75 zocks-p-0.75 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-200 zocks-border-0.5 align-items-center'>
          <i className='bi bi-info-square fs-6' />
          <div className='paragraph-small'>
            <span className='medium'>This query was stopped early.</span> Results shown are from {resultPercentage}% of clients.
          </div>
        </div>
      ) : null}
    </div>
  )
}
